import React from 'react';
import { Text, Flex, Button, LoadingOrbit, DataTable, Banner } from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Play, Trash, Bug, Check, Lock } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import type { EnvironmentInstance } from '../../../hooks/useEnvironmentInstances';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';
import { buildTunnelCommand } from './tunnel-command';
import { formatRelative } from './overview/time-format';
import { InstanceStatusBadge } from './InstanceStatusBadge';
import { HealthStatusBadge } from './HealthStatusBadge';

function CopyTunnelCommandButton({ ec2InstanceId }: { ec2InstanceId: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(buildTunnelCommand(ec2InstanceId));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail if page is not focused
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded text-muted-foreground hover:text-primary"
      title="Copy SSM tunnel command for OpenCode debug UI"
    >
      {copied ? <Check size={16} className="text-green-500" /> : <Bug size={16} />}
    </button>
  );
}

interface InstancesTabProps {
  template: EnvironmentTemplate;
  instances: EnvironmentInstance[];
  isLoading: boolean;
  isDeleting: boolean;
  onLaunch: () => void;
  onViewInstance: (instance: EnvironmentInstance) => void;
  onDelete: (instanceId: string) => void;
}

export const InstancesTab: React.FC<InstancesTabProps> = ({
  template,
  instances,
  isLoading,
  isDeleting,
  onLaunch,
  onViewInstance,
  onDelete,
}) => {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const canLaunchInstance = template.status === 'verified';

  const instanceColumns: ColumnDef<StockFeatures, EnvironmentInstance, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }: { row: { original: EnvironmentInstance } }) => (
        <Flex align="center" gap="xs">
          <Text weight="medium">{row.original.name}</Text>
          {row.original.isPrivate && <Lock size={14} className="text-muted-foreground" />}
        </Flex>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: { original: EnvironmentInstance } }) => (
        <InstanceStatusBadge status={row.original.status} />
      ),
    },
    {
      accessorKey: 'healthStatus',
      header: 'Health',
      cell: ({ row }: { row: { original: EnvironmentInstance } }) => (
        <HealthStatusBadge status={row.original.healthStatus} />
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }: { row: { original: EnvironmentInstance } }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatRelative(row.original.createdAt)}
        </Text>
      ),
    },
    {
      accessorKey: 'expiresAt',
      header: 'Expires',
      cell: ({ row }: { row: { original: EnvironmentInstance } }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatRelative(row.original.expiresAt)}
        </Text>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }: { row: { original: EnvironmentInstance } }) => {
        const instance = row.original;
        const isTerminated = instance.status === 'terminated' || instance.status === 'failed';
        const showDebug = isSuperadmin && instance.status === 'running' && instance.instanceId;

        if (!isTerminated && !showDebug) return null;

        return (
          <Flex gap="sm" justify="end">
            {showDebug && instance.instanceId && (
              <CopyTunnelCommandButton ec2InstanceId={instance.instanceId} />
            )}
            {isTerminated && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  onDelete(instance.id);
                }}
                aria-label="Delete"
                isDisabled={isDeleting}
              >
                <Trash size={16} />
              </Button>
            )}
          </Flex>
        );
      },
    },
  ];

  return (
    <div>
      <Flex align="center" justify="between" className="mb-4">
        <Text size="sm" className="text-muted-foreground">
          {instances.length} instance{instances.length !== 1 && 's'}
        </Text>
        <Button
          variant="primary"
          size="sm"
          onPress={onLaunch}
          isDisabled={!canLaunchInstance}
          iconLeft={<Play size={16} />}
        >
          Launch Instance
        </Button>
      </Flex>

      {!canLaunchInstance && (
        <Banner variant="warning" className="mb-4">
          Template must be verified before launching instances.
        </Banner>
      )}

      {isLoading ? (
        <Flex justify="center" className="py-8">
          <LoadingOrbit size="md" />
        </Flex>
      ) : instances.length === 0 ? (
        <Flex direction="column" align="center" className="py-8 text-center">
          <Text className="text-muted-foreground">No instances yet</Text>
          {canLaunchInstance && (
            <Text size="sm" className="text-muted-foreground mt-1">
              Launch an instance to create an isolated test environment.
            </Text>
          )}
        </Flex>
      ) : (
        <DataTable columns={instanceColumns} data={instances} onRowClick={onViewInstance} />
      )}
    </div>
  );
};
